import {
  Alert,
  AlertDescription,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import * as React from 'react';

import { HelpPopover, NotPermitted } from '@/design';
import { ApiError } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type {
  IEscalationLevels,
  IWorkItemAccount,
  IWorkItemAssignee
} from '../api/work-item-contracts';
import { workItemsApi } from '../api/work-items-api';
import { AdminPage, LoadError, TableSkeleton, useAdminData } from '../components/admin-page';
import { refusalMessage } from '../refusal';
import { forgetEscalationLevels } from './compliance/escalation';
import { NoteLine, sentence, unanswered, type ISaidNote } from './person-hub/said';

/**
 * Escalation levels (SR-410) — the hierarchy a work item's Escalate climbs, at
 * `/team/setup/escalation-levels`: level 1, level 2, …, each a few colleagues. survey-api moves an
 * escalated item to the next level and to that level's member with the fewest unclosed items, and
 * refuses a step past the last; with no level, Escalate keeps the item with its assignee.
 *
 * **The levels are one document**, edited here as a draft and saved whole (`PUT`, every level in
 * order): adding, removing and moving levels and members changes nothing until Save, and Cancel
 * puts back what was read. survey-api judges it — a level with nobody, an account on two levels, an
 * account no longer assignable — and each problem, keyed `levels[i]`, is said beside its level.
 *
 * **Read under `responses.browse`, written under `compliance.configure`** — the concern criteria's
 * permission, since both decide where an item goes: a reader without it sees the levels with every
 * control disabled and the reason said once. The picker offers whom an item can be assigned to
 * (`workItemAssignees`), as survey-api accepts no one else.
 *
 * A save forgets the queue's read of the levels (`forgetEscalationLevels`), so the next queue or
 * item opened holds Escalate off by the levels now saved.
 */

interface IDraftLevel {
  key: number;
  members: IWorkItemAccount[];
}

/** survey-api's problems with the last save: by the level's index, and the rest. */
interface IProblems {
  byLevel: ReadonlyMap<number, string[]>;
  rest: string[];
}

const NO_PROBLEMS: IProblems = { byLevel: new Map(), rest: [] };

export function AdminEscalationLevelsPage() {
  const { data, error, reload, adopt } = useAdminData<IEscalationLevels>((signal) =>
    workItemsApi.escalationLevels(signal)
  );
  const assignees = useAdminData<IWorkItemAssignee[]>((signal) => workItemsApi.workItemAssignees(signal));
  const permitted = useCan()('compliance.configure');
  const reasonId = React.useId();
  const nextKey = React.useRef(0);
  const draftOf = React.useCallback(
    (read: IEscalationLevels): IDraftLevel[] =>
      read.levels.map((level) => ({ key: nextKey.current++, members: level.members })),
    []
  );
  const [draft, setDraft] = React.useState<Nullable<IDraftLevel[]>>(null);
  const [problems, setProblems] = React.useState<IProblems>(NO_PROBLEMS);
  const [note, setNote] = React.useState<Nullable<ISaidNote>>(null);
  const [saving, setSaving] = React.useState(false);

  // A read that lands — the first, or the one after a save — is the draft's new start.
  React.useEffect(() => {
    if (data) setDraft(draftOf(data));
  }, [data, draftOf]);

  const levels = draft ?? [];
  const dirty =
    data !== null &&
    draft !== null &&
    JSON.stringify(draft.map((level) => level.members.map((member) => member.accountId))) !==
      JSON.stringify(data.levels.map((level) => level.members.map((member) => member.accountId)));
  const placed = new Set(levels.flatMap((level) => level.members.map((member) => member.accountId)));
  const free = (assignees.data ?? []).filter((account) => !placed.has(account.accountId));
  const off = !permitted || saving;

  // Any edit drops what the last save was told: a problem keyed by a level's place would sit under
  // whichever level moved there.
  const change = (next: IDraftLevel[]) => {
    setDraft(next);
    setNote(null);
    setProblems(NO_PROBLEMS);
  };
  const move = (from: number, to: number) => {
    const next = [...levels];
    const [level] = next.splice(from, 1);
    next.splice(to, 0, level);
    change(next);
  };
  const withMembers = (at: number, members: IWorkItemAccount[]) =>
    change(levels.map((level, index) => (index === at ? { ...level, members } : level)));

  const save = async () => {
    setSaving(true);
    setNote(null);
    setProblems(NO_PROBLEMS);
    try {
      const saved = await workItemsApi.saveEscalationLevels({
        levels: levels.map((level) => ({ accountIds: level.members.map((member) => member.accountId) }))
      });
      forgetEscalationLevels();
      adopt(saved);
      setNote({ tone: 'done', text: 'Saved.' });
    } catch (failure) {
      if (unanswered(failure)) {
        forgetEscalationLevels();
        setNote({
          tone: 'refused',
          text: 'The answer did not arrive — the levels have been read again: check whether they saved.'
        });
        void reload();
      } else {
        const listed = failure instanceof ApiError ? (failure.problem?.problems ?? []) : [];
        if (listed.length > 0) {
          const byLevel = new Map<number, string[]>();
          const rest: string[] = [];
          for (const problem of listed) {
            const at = /^levels\[(\d+)\]/.exec(problem.target);
            if (at) byLevel.set(Number(at[1]), [...(byLevel.get(Number(at[1])) ?? []), problem.reason]);
            else rest.push(problem.reason);
          }
          setProblems({ byLevel, rest });
        } else {
          setNote({ tone: 'refused', text: sentence(refusalMessage(failure, 'The levels were not saved')) });
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPage
      title="Escalation levels"
      crumb="Setup"
      actions={
        <HelpPopover label="About Escalation levels" align="end">
          <p>
            Escalate moves a work item one level up — from no level to level 1, then 2, and so on —
            and assigns it to that level’s member with the fewest open items. An item at the last
            level cannot be escalated again.
          </p>
          <p>No escalation levels — Escalate keeps the item with its assignee.</p>
          <p>
            A concern criterion that escalates what it raises puts the item at level 1. Reopening an
            item clears its level.
          </p>
          <p>
            Changes are a draft until Save; Cancel puts back the levels as saved. Each colleague can
            be on one level only.
          </p>
        </HelpPopover>
      }
    >
      {!permitted && <NotPermitted id={reasonId} what="editing escalation levels" />}
      {note && <NoteLine note={note} />}
      {assignees.error && (
        <p className="text-sm text-muted-foreground">
          The list of staff could not be read ({assignees.error}) — nobody can be added.
        </p>
      )}

      {error && data === null ? (
        <LoadError message={error} />
      ) : draft === null ? (
        <TableSkeleton rows={3} />
      ) : (
        <div className="grid max-w-3xl gap-3">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                Could not refresh — showing the levels as last read. {sentence(error)}
              </AlertDescription>
            </Alert>
          )}
          {levels.length === 0 ? (
            <p data-no-levels="" className="text-[0.8125rem] text-muted-foreground">
              No levels
            </p>
          ) : (
            <ol aria-label="Escalation levels" className="grid gap-3">
              {levels.map((level, index) => (
                <LevelCard
                  key={level.key}
                  number={index + 1}
                  members={level.members}
                  free={free}
                  problems={problems.byLevel.get(index) ?? []}
                  off={off}
                  describedBy={permitted ? undefined : reasonId}
                  first={index === 0}
                  last={index === levels.length - 1}
                  onMembers={(members) => withMembers(index, members)}
                  onUp={() => move(index, index - 1)}
                  onDown={() => move(index, index + 1)}
                  onRemove={() => change(levels.filter((_, at) => at !== index))}
                />
              ))}
            </ol>
          )}
          {problems.rest.length > 0 && (
            <ul role="alert" className="grid gap-1 text-sm text-destructive">
              {problems.rest.map((reason, index) => (
                <li key={index}>{reason}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={off}
              aria-describedby={permitted ? undefined : reasonId}
              onClick={() => change([...levels, { key: nextKey.current++, members: [] }])}
            >
              <Plus className="size-4" aria-hidden />
              Add level
            </Button>
            <span className="flex-1" />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={off || !dirty}
              onClick={() => {
                if (data) change(draftOf(data));
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={off || !dirty}
              aria-describedby={permitted ? undefined : reasonId}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

/** Whom the picker shows a level's add box with nothing chosen. */
const NOBODY = '';

/** One level: its number, its members as chips, the picker adding one, and its moves. */
function LevelCard({
  number,
  members,
  free,
  problems,
  off,
  describedBy,
  first,
  last,
  onMembers,
  onUp,
  onDown,
  onRemove
}: {
  number: number;
  members: IWorkItemAccount[];
  /** Whom no level holds yet — what the picker offers. */
  free: IWorkItemAssignee[];
  problems: string[];
  off: boolean;
  describedBy?: string;
  first: boolean;
  last: boolean;
  onMembers: (members: IWorkItemAccount[]) => void;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) {
  const label = `Level ${number}`;
  return (
    <li
      data-level={number}
      aria-label={label}
      className="grid gap-2 rounded-lg border border-border bg-white p-4 text-[0.8125rem]"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-[var(--color-royal-blue)]">{label}</span>
        <span className="flex-1" />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-7"
          disabled={off || first}
          aria-label={`Move ${label} up`}
          aria-describedby={describedBy}
          onClick={onUp}
        >
          <ArrowUp className="size-4" aria-hidden />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-7"
          disabled={off || last}
          aria-label={`Move ${label} down`}
          aria-describedby={describedBy}
          onClick={onDown}
        >
          <ArrowDown className="size-4" aria-hidden />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-7"
          disabled={off}
          aria-label={`Remove ${label}`}
          aria-describedby={describedBy}
          onClick={onRemove}
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>
      <ul aria-label={`Members of ${label}`} className="flex flex-wrap gap-1.5">
        {members.length === 0 && <li className="text-muted-foreground">Nobody yet</li>}
        {members.map((member) => (
          <li
            key={member.accountId}
            data-member={member.accountId}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-[var(--color-off-white)] py-0.5 pr-1 pl-2.5"
          >
            {member.email}
            <button
              type="button"
              disabled={off}
              aria-label={`Remove ${member.email} from ${label}`}
              aria-describedby={describedBy}
              onClick={() => onMembers(members.filter((one) => one.accountId !== member.accountId))}
              className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <X className="size-3" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <Select
        value={NOBODY}
        disabled={off || free.length === 0}
        onValueChange={(accountId) => {
          const account = free.find((one) => one.accountId === accountId);
          if (account) onMembers([...members, { accountId: account.accountId, email: account.email }]);
        }}
      >
        <SelectTrigger className="h-8 w-72 max-w-full" aria-label={`Add a member to ${label}`}>
          <SelectValue placeholder="Add a member…" />
        </SelectTrigger>
        <SelectContent>
          {free.map((account) => (
            <SelectItem key={account.accountId} value={account.accountId}>
              {account.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {problems.length > 0 && (
        <ul role="alert" className="grid gap-0.5 text-destructive">
          {problems.map((reason, index) => (
            <li key={index}>{reason}</li>
          ))}
        </ul>
      )}
    </li>
  );
}
